import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Settings, Mail, Save, RefreshCw, Shield, CheckCircle, AlertCircle } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const SoldiersSettingsEditor = ({ token, isSuperAdmin = false, canEdit = false }) => {
  const [settings, setSettings] = useState({
    application_email: "info@myhairchair.com",
    updated_at: null,
    updated_by: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newEmail, setNewEmail] = useState("");

  useEffect(() => {
    fetchSettings();
  }, [token]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/soldiers/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
      setNewEmail(res.data.application_email);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
      // Use defaults if not authorized
      if (error.response?.status === 403) {
        setSettings({
          application_email: "info@myhairchair.com",
          updated_at: null,
          updated_by: null
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!newEmail || !newEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/soldiers/settings`,
        { application_email: newEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Settings updated successfully!");
      setSettings(prev => ({
        ...prev,
        application_email: newEmail,
        updated_at: new Date().toISOString()
      }));
      setShowEditModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-700">
        <CardContent className="p-6 flex items-center justify-center">
          <RefreshCw className="w-5 h-5 text-amber-500 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  // If user can't edit, just show display version
  if (!isSuperAdmin && !canEdit) {
    return null;
  }

  return (
    <>
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Settings className="w-5 h-5 text-amber-500" />
            Soldiers Program Settings
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Configure where new soldier applications are sent
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-zinc-400">Application Notification Email</p>
                <p className="text-white font-medium">{settings.application_email}</p>
                {settings.updated_at && (
                  <p className="text-xs text-zinc-500 mt-1">
                    Last updated: {new Date(settings.updated_at).toLocaleDateString()} 
                    {settings.updated_by && ` by ${settings.updated_by}`}
                  </p>
                )}
              </div>
            </div>
            <Button
              onClick={() => {
                setNewEmail(settings.application_email);
                setShowEditModal(true);
              }}
              variant="outline"
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              data-testid="edit-soldiers-email-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </div>

          {isSuperAdmin && (
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-blue-400 mt-0.5" />
                <div>
                  <p className="text-blue-400 text-sm font-medium">Super Admin Access</p>
                  <p className="text-zinc-400 text-xs">
                    You can grant Super Soldiers the "edit_settings" permission to allow them to edit this email.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-500" />
              Edit Application Email
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              All new HairChair Soldier applications will be sent to this email address.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-300">Email Address</Label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="applications@yourdomain.com"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                data-testid="soldiers-email-input"
              />
            </div>

            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5" />
                <p className="text-amber-400 text-sm">
                  Make sure this email can receive messages. Application details including business info, 
                  license links, and contact information will be sent here.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditModal(false)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-black"
              data-testid="save-soldiers-email-btn"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SoldiersSettingsEditor;
